/*
 * Modal de drill-down multinivel (funnel): agrupa por claves encadenadas
 * hasta llegar al detalle de DOs. Réplica del engine de modales del HTML original.
 */
import { useEffect, useState } from "react";
import { Body, Box, Button, Description, Flexbox, Modal, ModalBody, ModalHeader, TableBasic, Title } from "codekit";

import { fmtCOP, keyLabels } from "./utils/carthelp";

/**
 * Agrupa un conjunto de filas por una clave sumando conteo y valor
 * @param {Array} mScope Filas del nivel actual
 * @param {string} sKey Clave de agrupacion
 * @returns {Array} Entradas [label, { count, valor }] ordenadas por valor desc
 */
const groupEntries = (mScope, sKey) => {
  const mGrouped = {};
  mScope.forEach((mRow) => {
    const sK = mRow[sKey] ?? "N/A";
    if (!mGrouped[sK]) mGrouped[sK] = { count: 0, valor: 0 };
    mGrouped[sK].count++;
    mGrouped[sK].valor += mRow.VALOR;
  });
  return Object.entries(mGrouped).sort((a, b) => b[1].valor - a[1].valor);
};

//Columnas del detalle final de DOs (ids sin espacios: se usan como grid-area)
const detailColumns = [
  { id: "DONROXXX", title: "DO", sorted: false, orderBy: null, center: false },
  { id: "CLIENTEX", title: "Cliente", sorted: false, orderBy: null, center: false },
  { id: "TIPOOPEX", title: "Tipo", sorted: false, orderBy: null, center: false },
  { id: "FECCREAX", title: "Fecha Creación", sorted: false, orderBy: null, center: true },
  { id: "FECLEVXX", title: "Fecha Levante", sorted: false, orderBy: null, center: true },
  { id: "DIASXXXX", title: "Días", sorted: false, orderBy: null, center: true },
  { id: "VALORXXX", title: "Valor", sorted: false, orderBy: null, center: false },
];

/**
 * Modal de drill-down que navega niveles encadenados de agrupación
 * @param {Object} props Propiedades del componente
 * @param {Object} props.initial Nivel inicial { scope, keys, breadcrumb }
 * @param {Function} props.onClose Cierra el modal
 */
const Funnelmd = ({ initial, onClose }) => {
  const [history, setHistory] = useState([initial]);

  useEffect(() => {
    setHistory([initial]);
  }, [initial]);

  const mState = history[history.length - 1];
  const { scope, keys, breadcrumb } = mState;

  //Baja un nivel filtrando por la etiqueta seleccionada
  const drillInto = (sLabel) => {
    const sKey = keys[0];
    const mNewScope = scope.filter((mRow) => (mRow[sKey] ?? "N/A") === sLabel);
    setHistory((mPrev) => [
      ...mPrev,
      { scope: mNewScope, keys: keys.slice(1), breadcrumb: [...breadcrumb, sLabel] },
    ]);
  };

  //Regresa un nivel en la navegación
  const goBack = () => setHistory((mPrev) => (mPrev.length > 1 ? mPrev.slice(0, -1) : mPrev));

  const bIsDetail = keys.length === 0;
  const sTitle = bIsDetail ? `Detalle de DOs (${scope.length})` : keyLabels[keys[0]] || keys[0];

  const groupColumns = [
    { id: "GRUPOXXX", title: keyLabels[keys[0]] || keys[0] || "", sorted: false, orderBy: null, center: false },
    { id: "CANTIDOX", title: "# DOs", sorted: false, orderBy: null, center: true },
    { id: "VALORXXX", title: "Valor", sorted: false, orderBy: null, center: false },
  ];
  const mGroupRows = bIsDetail
    ? []
    : groupEntries(scope, keys[0]).map(([sLabel, mV], nIndex) => ({
        id: nIndex,
        GRUPOXXX: sLabel,
        CANTIDOX: mV.count,
        VALORXXX: fmtCOP(mV.valor),
      }));
  const mDetailRows = bIsDetail
    ? [...scope]
        .sort((a, b) => b.VALOR - a.VALOR)
        .map((mRow, nIndex) => ({
          id: nIndex,
          DONROXXX: mRow.DO,
          CLIENTEX: mRow.NOMBRE,
          TIPOOPEX: mRow["TIPO DE OPERACIÓN"],
          FECCREAX: mRow._fechaCreacionStr,
          FECLEVXX: mRow._fechaLevanteStr,
          DIASXXXX: mRow._dias ?? "-",
          VALORXXX: fmtCOP(mRow.VALOR),
        }))
    : [];

  return (
    <Modal isOpen size="xxl" onClose={onClose}>
      <ModalHeader>
        <Flexbox justifyContent="between" alignItems="start">
          <Box>
            <Description variant="description-3" color="neutral-500">
              {breadcrumb.join(" › ")}
            </Description>
            <Title variant="title-2" color="accent-600">
              {sTitle}
            </Title>
          </Box>
          <Flexbox gap={2}>
            {history.length > 1 ? (
              <Button variant="secondary" size="m" onClick={goBack}>
                ‹ Atrás
              </Button>
            ) : null}
            <Button variant="secondary" icon="close" onlyIcon size="m" onClick={onClose} />
          </Flexbox>
        </Flexbox>
      </ModalHeader>
      <ModalBody>
        <Box spacing={4}>
          {bIsDetail ? (
            <TableBasic
              columns={detailColumns}
              data={mDetailRows}
              dataLength={mDetailRows.length}
              hasMore={false}
              fetchData={() => {}}
            />
          ) : (
            <TableBasic
              columns={groupColumns}
              data={mGroupRows}
              dataLength={mGroupRows.length}
              hasMore={false}
              fetchData={() => {}}
              onRowClick={({ row }) => drillInto(row.GRUPOXXX)}
            />
          )}
          <Box mt={2}>
            {!bIsDetail ? (
              <Description variant="description-3" color="neutral-500">
                Click en una fila para profundizar
              </Description>
            ) : null}
          </Box>
          <Box mt={3}>
            <Body variant="body-2" color="secondary-800">
              Total nivel: {fmtCOP(scope.reduce((a, r) => a + r.VALOR, 0))}
            </Body>
          </Box>
        </Box>
      </ModalBody>
    </Modal>
  );
};

export default Funnelmd;
